/** Symbols for the toolbar's symbol picker and Insert → Symbol. `math` symbols only work in maths mode. */
export interface LatexSymbol {
  char: string;
  latex: string;
  name: string;
  math: boolean;
}

const m = (char: string, latex: string, name: string): LatexSymbol => ({ char, latex, name, math: true });
const t = (char: string, latex: string, name: string): LatexSymbol => ({ char, latex, name, math: false });

export const SYMBOL_GROUPS: Array<{ name: string; symbols: LatexSymbol[] }> = [
  {
    name: "Greek",
    symbols: [
      m("α", "\\alpha", "alpha"),
      m("β", "\\beta", "beta"),
      m("γ", "\\gamma", "gamma"),
      m("δ", "\\delta", "delta"),
      m("ε", "\\varepsilon", "epsilon"),
      m("ϵ", "\\epsilon", "epsilon (lunate)"),
      m("ζ", "\\zeta", "zeta"),
      m("η", "\\eta", "eta"),
      m("θ", "\\theta", "theta"),
      m("ϑ", "\\vartheta", "theta (variant)"),
      m("ι", "\\iota", "iota"),
      m("κ", "\\kappa", "kappa"),
      m("λ", "\\lambda", "lambda"),
      m("μ", "\\mu", "mu"),
      m("ν", "\\nu", "nu"),
      m("ξ", "\\xi", "xi"),
      m("π", "\\pi", "pi"),
      m("ρ", "\\rho", "rho"),
      m("ϱ", "\\varrho", "rho (variant)"),
      m("σ", "\\sigma", "sigma"),
      m("τ", "\\tau", "tau"),
      m("υ", "\\upsilon", "upsilon"),
      m("φ", "\\varphi", "phi"),
      m("ϕ", "\\phi", "phi (straight)"),
      m("χ", "\\chi", "chi"),
      m("ψ", "\\psi", "psi"),
      m("ω", "\\omega", "omega"),
      m("Γ", "\\Gamma", "Gamma"),
      m("Δ", "\\Delta", "Delta"),
      m("Θ", "\\Theta", "Theta"),
      m("Λ", "\\Lambda", "Lambda"),
      m("Ξ", "\\Xi", "Xi"),
      m("Π", "\\Pi", "Pi"),
      m("Σ", "\\Sigma", "Sigma"),
      m("Υ", "\\Upsilon", "Upsilon"),
      m("Φ", "\\Phi", "Phi"),
      m("Ψ", "\\Psi", "Psi"),
      m("Ω", "\\Omega", "Omega"),
    ],
  },
  {
    name: "Operators",
    symbols: [
      m("±", "\\pm", "plus or minus"),
      m("∓", "\\mp", "minus or plus"),
      m("×", "\\times", "times"),
      m("÷", "\\div", "divide"),
      m("·", "\\cdot", "centre dot"),
      m("∗", "\\ast", "asterisk"),
      m("∘", "\\circ", "compose"),
      m("⊕", "\\oplus", "direct sum"),
      m("⊗", "\\otimes", "tensor product"),
      m("∑", "\\sum", "sum"),
      m("∏", "\\prod", "product"),
      m("∫", "\\int", "integral"),
      m("∬", "\\iint", "double integral"),
      m("∮", "\\oint", "contour integral"),
      m("∂", "\\partial", "partial"),
      m("∇", "\\nabla", "nabla"),
      m("√", "\\sqrt{}", "square root"),
      m("∪", "\\cup", "union"),
      m("∩", "\\cap", "intersection"),
      m("⋃", "\\bigcup", "big union"),
      m("⋂", "\\bigcap", "big intersection"),
      m("∧", "\\wedge", "and"),
      m("∨", "\\vee", "or"),
      m("¬", "\\neg", "not"),
    ],
  },
  {
    name: "Relations",
    symbols: [
      m("≤", "\\leq", "less than or equal"),
      m("≥", "\\geq", "greater than or equal"),
      m("≠", "\\neq", "not equal"),
      m("≈", "\\approx", "approximately"),
      m("≡", "\\equiv", "equivalent"),
      m("∼", "\\sim", "similar"),
      m("≃", "\\simeq", "similar or equal"),
      m("≅", "\\cong", "congruent"),
      m("∝", "\\propto", "proportional to"),
      m("≪", "\\ll", "much less than"),
      m("≫", "\\gg", "much greater than"),
      m("∈", "\\in", "element of"),
      m("∉", "\\notin", "not an element of"),
      m("∋", "\\ni", "contains"),
      m("⊂", "\\subset", "subset"),
      m("⊆", "\\subseteq", "subset or equal"),
      m("⊃", "\\supset", "superset"),
      m("⊇", "\\supseteq", "superset or equal"),
      m("∥", "\\parallel", "parallel"),
      m("⊥", "\\perp", "perpendicular"),
      m("∣", "\\mid", "divides"),
      m("≺", "\\prec", "precedes"),
      m("≻", "\\succ", "succeeds"),
      m("≜", "\\triangleq", "defined as"),
    ],
  },
  {
    name: "Arrows",
    symbols: [
      m("→", "\\rightarrow", "right arrow"),
      m("←", "\\leftarrow", "left arrow"),
      m("↔", "\\leftrightarrow", "left-right arrow"),
      m("⇒", "\\Rightarrow", "implies"),
      m("⇐", "\\Leftarrow", "implied by"),
      m("⇔", "\\Leftrightarrow", "if and only if"),
      m("↦", "\\mapsto", "maps to"),
      m("⟶", "\\longrightarrow", "long right arrow"),
      m("⟹", "\\Longrightarrow", "long implies"),
      m("↑", "\\uparrow", "up arrow"),
      m("↓", "\\downarrow", "down arrow"),
      m("↕", "\\updownarrow", "up-down arrow"),
      m("↗", "\\nearrow", "north-east arrow"),
      m("↘", "\\searrow", "south-east arrow"),
      m("⇀", "\\rightharpoonup", "harpoon"),
      m("⇌", "\\rightleftharpoons", "equilibrium"),
    ],
  },
  {
    name: "Misc",
    symbols: [
      m("∞", "\\infty", "infinity"),
      m("∅", "\\emptyset", "empty set"),
      m("∀", "\\forall", "for all"),
      m("∃", "\\exists", "there exists"),
      m("∄", "\\nexists", "there does not exist"),
      m("ℝ", "\\mathbb{R}", "real numbers"),
      m("ℕ", "\\mathbb{N}", "natural numbers"),
      m("ℤ", "\\mathbb{Z}", "integers"),
      m("ℚ", "\\mathbb{Q}", "rationals"),
      m("ℂ", "\\mathbb{C}", "complex numbers"),
      m("ℏ", "\\hbar", "h-bar"),
      m("ℓ", "\\ell", "script l"),
      m("ℜ", "\\Re", "real part"),
      m("ℑ", "\\Im", "imaginary part"),
      m("′", "\\prime", "prime"),
      m("°", "^\\circ", "degrees"),
      m("⋯", "\\cdots", "centred dots"),
      m("⋮", "\\vdots", "vertical dots"),
      m("⋱", "\\ddots", "diagonal dots"),
      m("∴", "\\therefore", "therefore"),
      m("∵", "\\because", "because"),
      m("□", "\\square", "square"),
      m("∠", "\\angle", "angle"),
      m("△", "\\triangle", "triangle"),
    ],
  },
  {
    name: "Text",
    symbols: [
      t("…", "\\ldots", "ellipsis"),
      t("–", "--", "en dash"),
      t("—", "---", "em dash"),
      t("“”", "``''", "quotes"),
      t("§", "\\S", "section"),
      t("¶", "\\P", "paragraph"),
      t("©", "\\copyright", "copyright"),
      t("®", "\\textregistered", "registered"),
      t("™", "\\texttrademark", "trademark"),
      t("°", "\\textdegree", "degree"),
      t("€", "\\euro", "euro (eurosym)"),
      t("£", "\\pounds", "pound"),
      t("†", "\\dag", "dagger"),
      t("‡", "\\ddag", "double dagger"),
      t("•", "\\textbullet", "bullet"),
      t("%", "\\%", "percent"),
      t("&", "\\&", "ampersand"),
      t("#", "\\#", "hash"),
      t("$", "\\$", "dollar"),
      t("_", "\\_", "underscore"),
      t("~", "\\textasciitilde", "tilde"),
      t("\\", "\\textbackslash", "backslash"),
      t("␣", "~", "non-breaking space"),
      t("µ", "\\textmu", "micro"),
    ],
  },
];
